/**
 * WebSocket Disabler for Production
 * This utility disables application WebSocket connections in production
 * until the backend is properly configured. Third-party calling transport
 * must remain available for the Twilio Voice SDK.
 */

export function isTwilioWebSocketUrl(value: string | URL): boolean {
  try {
    const url = value instanceof URL ? value : new URL(String(value), window.location.origin);
    return (
      url.protocol === 'wss:' &&
      (url.hostname === 'twilio.com' || url.hostname.endsWith('.twilio.com'))
    );
  } catch {
    return false;
  }
}

export function disableWebSocketInProduction() {
  // Only disable in production
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  // Preserve Twilio's secure signaling transport while application sockets
  // remain disabled. Twilio Voice connects to regional *.twilio.com gateways.
  const OriginalWebSocket = window.WebSocket;
  const createBlockedWebSocket = (url: string | URL) => {
    console.debug('[WebSocket] Connection blocked in production:', url);

    // Return a mock WebSocket that does nothing
    const mock = {
      CONNECTING: 0,
      OPEN: 1,
      CLOSING: 2,
      CLOSED: 3,
      url,
      readyState: 3, // CLOSED
      bufferedAmount: 0,
      onopen: null,
      onerror: null,
      onclose: null as WebSocket['onclose'],
      onmessage: null,
      protocol: '',
      extensions: '',
      binaryType: 'blob' as WebSocket['binaryType'],
      close: () => {},
      send: () => {},
      addEventListener: () => {},
      removeEventListener: () => {},
      dispatchEvent: () => true,
    };

    // Call onclose after a delay to simulate connection failure
    setTimeout(() => {
      const onClose = mock.onclose;
      if (onClose) {
        onClose.call(
          mock as WebSocket,
          new CloseEvent('close', {
            code: 1006,
            reason: 'WebSocket disabled in production',
            wasClean: false,
          }),
        );
      }
    }, 100);

    return mock as WebSocket;
  };

  window.WebSocket = new Proxy(OriginalWebSocket, {
    construct(Target, args) {
      const [url] = args as [string | URL, string | string[] | undefined];
      if (isTwilioWebSocketUrl(url)) {
        console.debug('[WebSocket] Twilio signaling allowed:', String(url));
        return Reflect.construct(Target, args) as WebSocket;
      }
      return createBlockedWebSocket(url);
    },
  });

  console.log('[WebSocket] Application sockets disabled; Twilio signaling allowed');
}
